import {createSnackbar} from 'Core/ducks/snackbar';
import {Async, AsyncResult, Empty, Loaded, Loading, ServerError, isLoaded} from 'Core/models/Async';
import {QuotingRouteConstants} from 'Quoting/Routes';
import QuotingAPI from 'Quoting/api/QuotingAPI';
import {
  deleteState,
  getCompensationPayload,
  getEvaluatePayload,
  getHeapedRule,
  getLevelRule,
} from 'Quoting/api/apiHelpers';
import {CompensationProcessingType, PredefinedHierarchicalContracts} from 'Quoting/api/types';
import {
  CompensationActionType,
  deleteCompensation,
  deleteCompensationFlag,
  getAgencyCompensation,
  getCompensationInit,
  getExistingCompensationDetails,
  getPredefinedHierarchicalContracts,
  getValidatePayloadDraft,
  getValidatePayloadSave,
  refreshProducerLevels,
  setCompensationValue,
  setExistingCompensationDetails,
  setIsCompensationUpdated,
  setPredefinedHierarchicalContracts,
  updateAgencyCompensation,
  updateCompensationInit,
  updateValidatePayloadDraft,
} from 'Quoting/ducks/compensation';
import {
  changeAttributeValues,
  removeQuoteFormErrorEntry,
  setQuotedCommissionFlatPercentRate,
  setQuotedCommissionHeapedPercentRate,
  updateIsSavingQuote,
  updateMasterPolicyQuote,
  updateQuote,
} from 'Quoting/ducks/createQuote';
import {evaluateDecisionTable} from 'Quoting/ducks/quoteActions';
import {
  CompensationPayload,
  CompensationValue,
  DecisionHeapedTable,
  EvaluateLevelPayload,
  Heaped,
  Level,
  RetrieveCompensationPayload,
  SearchCompensation,
} from 'Quoting/models/DTOs/CompensationPayload';
import {InitDto} from 'Quoting/models/DTOs/Init';
import {PremiumSplitDto} from 'Quoting/models/DTOs/PremiumSplit';
import {MasterPolicyQuote} from 'Quoting/models/DTOs/quote';
import {A360ProductModel, LevelValues} from 'Quoting/models/Quoting';
import {isDefined, parsePath} from 'Quoting/utils/utilities';
import {push} from 'connected-react-router';
import {isRight} from 'fp-ts/lib/Either';
import {Map as ImmutableMap, fromJS} from 'immutable';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {createOptionsForAttributes, getQuotedCompensationRates} from './apiResponseUtilities';

function* fetchPremiumSplit(action: ReturnType<typeof getAgencyCompensation>) {
  const errorMessage = 'Error: could not fetch Agency & Compensation Details!';
  const {quote} = action.payload;
  const params: string[] = quote.premiumSplit._uri?.split('/')?.slice(5);
  try {
    yield put(updateAgencyCompensation(new Loading()));

    const response: AsyncResult<PremiumSplitDto> = yield call(
      QuotingAPI.getPremiumSplitDetails,
      params[0],
      Number.parseInt(params[1])
    );
    if (isRight(response)) {
      yield put(updateAgencyCompensation(new Loaded(response.right)));
      for (let split of response.right.splitParts) {
        const payload: SearchCompensation = {
          productCode: quote.productCd,
          policyNumber: quote.policyNumber,
          agencyCode:
            split?.premiumSplitDetails?.[0]?.broker?.contractCd ?? split?.premiumSplitDetails?.[0]?.broker?.agencyName,
          slotId: '1',
          premiumSplitId: split._key.id,
          transactionEffectiveDate: quote.transactionDetails.txEffectiveDate,
          policyEffectiveDate: quote.termDetails.termEffectiveDate,
          level: LevelValues.Policy,
        };
        yield put(getExistingCompensationDetails(payload, quote));
      }
    } else {
      yield put(updateAgencyCompensation(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(updateAgencyCompensation(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchCompensationInit(action: ReturnType<typeof getCompensationInit>) {
  const errorMessage = 'Error: could not fetch Initial Compensation Details!';
  try {
    yield put(updateCompensationInit(new Loading()));

    const response: AsyncResult<InitDto> = yield call(QuotingAPI.getCompensationInitDetails, {});
    if (isRight(response)) {
      yield put(updateCompensationInit(new Loaded(response.right)));
      yield put(setExistingCompensationDetails(new Loaded([])));
    } else {
      yield put(updateCompensationInit(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(updateCompensationInit(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchCompensationDraft(action: ReturnType<typeof getValidatePayloadDraft>) {
  const errorMessage = 'Error: could not draft Compensation Details!';
  const {payload} = action.payload;
  try {
    yield put(updateValidatePayloadDraft(new Loading()));
    const compPayload: CompensationPayload = deleteState(payload);
    const response: AsyncResult<CompensationPayload> = yield call(QuotingAPI.validatePayloadDraft, compPayload);
    if (isRight(response)) {
      yield put(updateValidatePayloadDraft(new Loaded(response.right)));
    } else {
      yield put(updateValidatePayloadDraft(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(updateValidatePayloadDraft(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchCompensationSave(action: ReturnType<typeof getValidatePayloadSave>) {
  const errorMessage = 'Error: could not save Compensation Details!';
  let {payload, shouldSaveQuote} = action.payload;
  const quote: Async<ImmutableMap<string, any>> = yield select((state: ReducerState) => state.quoteForm.quote);
  const {searchValue, initResponse, premiumSplitSummary, compensationValue} = yield select(
    (state: ReducerState) => state.compensation
  );
  const splitPart = premiumSplitSummary.Data?.splitParts[0];

  let quoteData = isLoaded(quote) ? quote.Data : ImmutableMap<string, any>();
  if (!isDefined(payload)) {
    if (isLoaded(searchValue) || isLoaded(initResponse)) {
      const policyNumberValue: string = quoteData.get('policyNumber');

      const compPayload: RetrieveCompensationPayload = {
        policyNumberValue: policyNumberValue,
        start:
          quoteData.get('rateEffectiveDate')?.split('T')[0] ??
          (quoteData.getIn(parsePath('termDetails.termEffectiveDate')) as string)?.split('T')[0] ??
          '',
        initResponse: isLoaded(initResponse) ? initResponse.Data : searchValue.Data[0],
        premiumSplitPercentage: splitPart?.premiumSplitPct * 100,
        productCode: quoteData.get('productCd'),
        code: quoteData.getIn(parsePath('agencyDetails[0].agencyName')) as string,
        premiumSplitId: splitPart?._key.id,
        rate: compensationValue.rate,
        compType: compensationValue.type,
      };

      payload = getCompensationPayload(compPayload);
    }
  } else {
    payload.contract.processingDefinition.calculationDefinition.rule =
      compensationValue.type === 'Level'
        ? (getLevelRule(compensationValue.rate) as Level)
        : (getHeapedRule(compensationValue.rate) as Heaped);
  }

  try {
    yield put(updateValidatePayloadDraft(new Loading()));
    const compPayload: CompensationPayload = deleteState(payload);
    const response: AsyncResult<CompensationPayload> = yield call(QuotingAPI.validatePayloadDraft, compPayload);
    if (isRight(response)) {
      const saveResponse: AsyncResult<CompensationPayload> = yield call(
        QuotingAPI.validatePayloadSave,
        response.right,
        searchValue?.Data ?? []
      );

      if (isRight(saveResponse)) {
        yield put(removeQuoteFormErrorEntry('compensation'));
        yield put(updateValidatePayloadDraft(new Loaded(saveResponse.right)));
        yield put(setExistingCompensationDetails(new Loaded([saveResponse.right])));

        yield put(updateIsSavingQuote(true));

        const {
          isCompensationUpdated,
          compensationValue,
        }: {isCompensationUpdated: boolean; compensationValue: CompensationValue} = yield select(
          (state: ReducerState) => state.compensation
        );

        const {decisionTableHeapedResponse}: {decisionTableHeapedResponse: DecisionHeapedTable[]} = yield select(
          (state: ReducerState) => state.quoteActions
        );

        const quoteFormErrors: Record<string, string> = yield select(
          (state: ReducerState) => state.quoteForm.quoteFormErrors
        );

        let levelEquivalent = quoteData.get('levelEquivalent');

        if (compensationValue.type === 'Heaped') {
          levelEquivalent = Number(
            decisionTableHeapedResponse?.find(v => v.heapedCommission === compensationValue.rate)?.levelEquivalent
          );
        }

        if (shouldSaveQuote) {
          const updateQuoteResponse: AsyncResult<MasterPolicyQuote> = yield call(
            QuotingAPI.updateQuote,
            quoteData.get('_modelName') as A360ProductModel,
            quoteData,
            {
              compensationRateImpactOccurred: isCompensationUpdated,
              levelEquivalent: compensationValue.type === 'Heaped' ? levelEquivalent : undefined,
            }
          );

          if (isRight(updateQuoteResponse)) {
            yield put(createSnackbar('Quote saved successfully.'));
            yield put(updateMasterPolicyQuote(new Loaded(fromJS(updateQuoteResponse.right) as any)));
            const isCompensationAdded = isDefined(compensationValue.rate) && compensationValue.rate.trim() !== '';
            const compensationErrorKey = Object.entries(quoteFormErrors)?.find(([key, value]) =>
              value?.includes('Compensation')
            )?.[0];

            if (isCompensationAdded) {
              yield put(removeQuoteFormErrorEntry(compensationErrorKey));
            }
          } else {
            yield put(createSnackbar('Quote save failed!'));
          }

          if (action.payload.shouldExit && action.payload.opportunityId) {
            yield put(push(QuotingRouteConstants.OPPORTUNITY_SUMMARY(action.payload.opportunityId)));
          }
        }
      } else {
        yield put(updateValidatePayloadDraft(new ServerError(errorMessage)));
        yield put(createSnackbar(errorMessage));
      }
    } else {
      yield put(updateValidatePayloadDraft(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(updateValidatePayloadDraft(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(updateIsSavingQuote(false));
    yield put(setIsCompensationUpdated(false));
    if (action.payload.onComplete) {
      action.payload.onComplete();
    }
  }
}

function* deleteCompensationDetails(action: ReturnType<typeof deleteCompensation>) {
  const errorMessage = 'Error: could not delete Compensation Details!';
  const successMessage = 'Compensation successfully deleted';
  const {payload} = action.payload;
  try {
    yield put(updateValidatePayloadDraft(new Loading()));
    const response: AsyncResult<CompensationPayload> = yield call(QuotingAPI.deleteCompensation, payload);
    if (isRight(response)) {
      yield put(updateValidatePayloadDraft(new Empty()));
      yield put(deleteCompensationFlag(false));
      yield put(setCompensationValue({type:'', rate:''}))
      yield put(setExistingCompensationDetails(new Loaded([])));
      yield put(createSnackbar(successMessage));
      yield put(updateIsSavingQuote(true));
      yield put(updateQuote(action.payload.shouldExit, action.payload.opportunityId));
    } else {
      yield put(updateValidatePayloadDraft(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(updateValidatePayloadDraft(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(updateIsSavingQuote(false));
    yield put(setIsCompensationUpdated(false));
  }
}

function* searchCompensationDetails(action: ReturnType<typeof getExistingCompensationDetails>) {
  const errorMessage = 'Error: could not search Compensation Details!';
  const {payload, quote} = action.payload;
  try {
    const response: AsyncResult<CompensationPayload[]> = yield call(QuotingAPI.getExistingCompensationDetails, payload);
    if (isRight(response)) {
      const {selectedPlan} = yield select((state: ReducerState) => state.quoteForm);
      if (isDefined(selectedPlan)) {
        const evaluatePayload = {
          packageCd: quote.benefitPackagingDetail?.packageCd,
          riskStateCd: quote?.riskStateCd,
          planCd: quote?.benefitPackagingDetail?.plans[selectedPlan?.planIndex ?? 0]?.planCd,
          rateRequestDate: quote?.rateRequestDate,
          termEffectiveDate: quote?.termDetails?.termEffectiveDate,
          techFeePct: quote?.techPartners?.[0]?.techFeePct ?? 0,
          coverageBasisCd:
            quote?.benefitPackagingDetail?.plans[selectedPlan?.planIndex ?? 0]?.coverageDefs[0]?.coverageBasisCd,
        };

        yield put(
          evaluateDecisionTable(
            getEvaluatePayload(evaluatePayload, 'Level') as EvaluateLevelPayload,
            getEvaluatePayload(evaluatePayload, 'Heaped'),
            quote.productCd
          )
        );
      }

      yield put(setExistingCompensationDetails(new Loaded(response.right)));

      if (response.right.length > 0) {
        const compType =
          response.right[0].contract.processingDefinition?.calculationDefinition.rule._type === 'FlatContractRuleEntity'
            ? 'Level'
            : 'Heaped';
        const checkInt = (rate: number) => (Number.isInteger(rate) ? Number(rate).toFixed(1) : rate);
        const compRate =
          compType === 'Level'
            ? `${(response.right[0].contract.processingDefinition?.calculationDefinition.rule as Level)?.rate?.rate}`
            : `${checkInt(
                // @ts-ignore
                response.right[0].contract.processingDefinition?.calculationDefinition.rule.bounds[0].rate
              )} / ${checkInt(
                // @ts-ignore
                response.right[0].contract.processingDefinition?.calculationDefinition.rule.bounds[1].rate
              )}`;

        const compValue: CompensationValue = {type: compType, rate: compRate};
        yield put(setCompensationValue(compValue));
        yield put(updateValidatePayloadDraft(new Loaded(response.right[0])));
      }
    } else {
      yield put(setExistingCompensationDetails(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchPredefinedHierarchicalContractsFromAPI(action: ReturnType<typeof getPredefinedHierarchicalContracts>) {
  const errorMessage = 'Error: could not fetch compensation contract!';

  try {
    const response: AsyncResult<PredefinedHierarchicalContracts> = yield call(
      QuotingAPI.fetchPredefinedHierarchicalContracts,
      action.payload.quote
    );
    if (isRight(response)) {
      const ratesAndProcessingType = getQuotedCompensationRates(response.right);
      yield put(setPredefinedHierarchicalContracts(response.right, ratesAndProcessingType?.processingType));

      if (ratesAndProcessingType?.processingType === CompensationProcessingType.FlatPercent) {
        yield put(setQuotedCommissionFlatPercentRate(ratesAndProcessingType.quotedCommissionRate));
      } else if (ratesAndProcessingType?.processingType === CompensationProcessingType.IndividualHeapedPercent) {
        yield put(
          setQuotedCommissionHeapedPercentRate(
            ratesAndProcessingType.quotedCommissionFYRate,
            ratesAndProcessingType.quotedCommissionRenewalRate
          )
        );
      }
    } else {
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* refreshProducerLevelsForYSeries(action: ReturnType<typeof refreshProducerLevels>) {
  try {
    const response: AsyncResult<any> = yield call(
      QuotingAPI.refreshProducerLevels,
      action.payload.productModel,
      action.payload.quotedCommissionSeries
    );
    if (isRight(response)) {
      const options = createOptionsForAttributes(response.right?.[0]?.valueListItems);
      const key = `${response.right?.[0]?.entityName}.${response.right?.[0]?.attributeName}`;
      yield put(changeAttributeValues(ImmutableMap<string, any>().set(key, options)));
    }
  } catch (error) {
    console.error(error);
  }
}

function* fetchPremiumSplitSaga() {
  yield takeLatest(CompensationActionType.GET_AGENCY_COMPENSATION, fetchPremiumSplit);
}

function* fetchCompensationInitSaga() {
  yield takeLatest(CompensationActionType.GET_COMPENSATION_INIT, fetchCompensationInit);
}

function* fetchCompensationDraftSaga() {
  yield takeLatest(CompensationActionType.GET_COMPENSATION_PAYLOAD, fetchCompensationDraft);
}

function* fetchCompensationSaveSaga() {
  yield takeLatest(CompensationActionType.SAVE_COMPENSATION_PAYLOAD, fetchCompensationSave);
}

function* deleteCompensationSaga() {
  yield takeLatest(CompensationActionType.DELETE_COMPENSATION, deleteCompensationDetails);
}

function* searchCompensationSaga() {
  yield takeLatest(CompensationActionType.SEARCH_COMPENSATION, searchCompensationDetails);
}

function* predefinedHierarchicalContractsSaga() {
  yield takeLatest(
    CompensationActionType.GET_PREDEFINED_HIERARCHICAL_CONTRACTS,
    fetchPredefinedHierarchicalContractsFromAPI
  );
}

function* refreshProducerLeveslForYSeriesSaga() {
  yield takeLatest(CompensationActionType.REFRESH_PRODUCER_LEVELS, refreshProducerLevelsForYSeries);
}

export default function* compensationSaga() {
  yield all([
    fetchPremiumSplitSaga(),
    fetchCompensationInitSaga(),
    fetchCompensationDraftSaga(),
    fetchCompensationSaveSaga(),
    deleteCompensationSaga(),
    searchCompensationSaga(),
    predefinedHierarchicalContractsSaga(),
    refreshProducerLeveslForYSeriesSaga(),
  ]);
}
